import { AlertTriangle } from "lucide-react"
import type { IssueConsequence, OnboardingValidationIssue } from "@/api/onboarding-api.types"
import { useTranslation } from "@/hooks/use-translation"
import { fieldLabelKey, shortRule, type Translate } from "@/lib/issue-text"
import {
  groupIssuesByRecord,
  type IssueFixGroup,
  type IssueRecord,
  type IssueRecordContext,
} from "@/lib/issue-records"

export interface IssuePanelProps {
  issues: OnboardingValidationIssue[]
  context: IssueRecordContext
  tone: "amber" | "red"
  /** Whether the record list is shown. Ignored while confirming, where the list is the step. */
  open: boolean
  onToggle?: () => void
  /** The confirmation step: the heading changes, the toggle goes, every group shows its consequence. */
  confirming: boolean
  /** The FBO app the ATS Hub link opens in. */
  fboAppUrl: string
  /** Inside a box that already has a frame and an icon: the panel draws neither. */
  embedded?: boolean
}

const PALETTE = {
  amber: {
    box: "bg-amber-50 border-amber-200 text-amber-900",
    icon: "text-amber-500",
    muted: "text-amber-700",
    rule: "border-amber-200",
    toggle: "text-amber-700 hover:text-amber-900",
  },
  red: {
    box: "bg-red-50 border-red-200 text-red-900",
    icon: "text-red-500",
    muted: "text-red-700",
    rule: "border-red-200",
    toggle: "text-red-700 hover:text-red-900",
  },
} as const

type Palette = (typeof PALETTE)[keyof typeof PALETTE]

function countText(count: number, t: Translate): string {
  return count === 1 ? t("valuesCannotBeAcceptedOne") : t("valuesCannotBeAcceptedMany", { count })
}

function IssueLine({
  issue,
  t,
  groupConsequence,
}: {
  issue: OnboardingValidationIssue
  t: Translate
  /** The consequence the group states for all its lines; a lighter one is said on the line itself. */
  groupConsequence?: IssueConsequence
}) {
  const key = fieldLabelKey(issue.field)
  const notSaved = issue.consequence === "ValueNotStored" && groupConsequence !== undefined && groupConsequence !== "ValueNotStored"
  return (
    <li className="break-words">
      <span className="font-medium">{key ? t(key) : issue.field}</span>
      {issue.incomingValue ? <> · <code>{issue.incomingValue}</code></> : null}
      <> · {shortRule(issue.rule, t)}</>
      {issue.consequence === "ValueReplaced" ? (
        <> · {t("willBeSavedAs")} <code>{issue.resultingValue || t("nothing")}</code></>
      ) : null}
      {notSaved ? <> · {t("willNotBeSaved")}</> : null}
    </li>
  )
}

function FixLead({ group, t, palette, fboAppUrl }: { group: IssueFixGroup; t: Translate; palette: Palette; fboAppUrl: string }) {
  if (group.fixLocation === "Fbo") {
    // Only a value that leaves the organization incomplete has something to finish in FBO. A value that is
    // simply not saved there has no action, so the line would send the reviewer to complete nothing.
    return group.consequence === "RecordIncomplete" ? <div className={palette.muted}>{t("fixInFbo")}</div> : null
  }

  const fix = group.fixLocation === "AtsFieldMapping" ? (
    <a href={`${fboAppUrl}/next/admin/ats-management`} target="_blank" rel="noreferrer" className="underline font-medium">
      {t("fixInAtsMapping")}
    </a>
  ) : (
    <span className="font-medium">{t("fixInAtsThenResync")}</span>
  )

  return <div className={palette.muted}>{fix} · {t("orContinueWithoutThem")}</div>
}

function Consequence({ group, t, palette }: { group: IssueFixGroup; t: Translate; palette: Palette }) {
  switch (group.consequence) {
    case "RecordIncomplete":
      return <div className={palette.muted}>{t("consequenceRecordIncomplete")}</div>
    case "ValueNotStored":
      return <div className={palette.muted}>{t("consequenceValueNotStored")}</div>
    default:
      return null
  }
}

function RecordBlock({
  record,
  t,
  palette,
  confirming,
  fboAppUrl,
  embedded,
}: {
  record: IssueRecord
  t: Translate
  palette: Palette
  confirming: boolean
  fboAppUrl: string
  embedded: boolean
}) {
  const hasBlocking = record.blocking.length > 0
  // The organization block always has a name to show: the server's, the drawer's, or the plain label.
  const name = record.name || (record.kind === "organization" ? t("fieldOrganization") : "")
  return (
    <div className={`border-t ${palette.rule} ${embedded ? "px-0" : "px-3"} py-2.5 space-y-1.5`}>
      <div>
        <span className="font-semibold">{name}</span>
        <span className={hasBlocking ? "text-red-700 font-semibold" : `${palette.muted} font-semibold`}>
          {" · "}{countText(record.count, t)}
        </span>
      </div>
      {record.atsId ? (
        <div className={palette.muted}>
          {record.kind === "worker" ? t("recordWorker", { id: record.atsId }) : t("recordSupplier", { id: record.atsId })}
        </div>
      ) : null}

      {hasBlocking ? (
        <div className="text-red-800">
          <div className="text-red-700 font-medium">{t("recordNotCreated")}</div>
          <ul className="pl-5 list-disc space-y-0.5">
            {record.blocking.map((issue) => <IssueLine key={`${issue.field}|${issue.rule}`} issue={issue} t={t} />)}
          </ul>
        </div>
      ) : null}

      {record.fixes.map((group) => (
        <div key={group.fixLocation} className="space-y-0.5">
          <FixLead group={group} t={t} palette={palette} fboAppUrl={fboAppUrl} />
          {confirming || group.consequence === "RecordIncomplete" ? <Consequence group={group} t={t} palette={palette} /> : null}
          <ul className="pl-5 list-disc space-y-0.5">
            {group.issues.map((issue) => (
              <IssueLine key={`${issue.field}|${issue.rule}`} issue={issue} t={t} groupConsequence={group.consequence} />
            ))}
          </ul>
        </div>
      ))}
    </div>
  )
}

/**
 * The preflight's findings, by record. Closed, it is one line and a toggle; open, each record the submit
 * would create lists what cannot be accepted about it and where that is fixed.
 */
export function IssuePanel({ issues, context, tone, open, onToggle, confirming, fboAppUrl, embedded = false }: IssuePanelProps) {
  const { t } = useTranslation()
  const palette = PALETTE[tone]
  const records = groupIssuesByRecord(issues, context)
  const showList = confirming || open
  const frame = embedded ? "" : `mb-3 rounded-lg border ${palette.box}`

  return (
    <div className={`text-xs leading-relaxed ${frame}`.trim()} role="alert">
      <div className={`flex gap-2 items-start ${embedded ? "pb-1.5" : "px-3 py-2.5"}`}>
        {embedded ? null : <AlertTriangle size={15} className={`${palette.icon} shrink-0 mt-0.5`} />}
        <span className="font-medium">{confirming ? t("beforeYouContinue") : countText(issues.length, t)}</span>
        {onToggle && !confirming ? (
          <>
            <span className="flex-1" />
            <button
              type="button"
              onClick={onToggle}
              aria-expanded={open}
              className={`font-medium underline whitespace-nowrap ${palette.toggle}`}
            >
              {open ? t("hideDetails") : t("showDetails")}
            </button>
          </>
        ) : null}
      </div>
      {showList ? (
        <div className="max-h-[240px] overflow-y-auto">
          {records.map((record) => (
            <RecordBlock key={record.key} record={record} t={t} palette={palette} confirming={confirming} fboAppUrl={fboAppUrl} embedded={embedded} />
          ))}
        </div>
      ) : null}
    </div>
  )
}
